<script lang="ts">
  import type { Action } from "svelte/action"

  type Size = "sm" | "md" | "lg"
  type TextAlign = "center" | "left"
  type Color = "primary" | "secondary" | "link"

  export let color: Color = "primary"
  export let fullWidth: boolean = false
  export let size: Size = "md"
  export let disabled: boolean = false
  export let textAlign: TextAlign = "center"
  export let onClick: (e: KeyboardEvent | MouseEvent) => void
  export let use: Action<HTMLElement, any>[] = []
  export let className: string = ""
  export let ariaDescribedby: string | undefined = undefined
  /**
   * If true, the component will not try to set the button icon color
   */
  export let allowIconOwnColor: boolean = false

  const combinedActions = (node: HTMLElement) => {
    const destructors = use.map((action) => {
      return action(node)
    })

    return {
      destroy() {
        destructors.forEach((destructor) => {
          if (typeof destructor?.destroy == "function") destructor.destroy()
        })
      },
    }
  }
</script>

<span
  class="au-button {className}"
  tabindex="0"
  role="button"
  data-size={size}
  data-color={color}
  data-full-width={fullWidth}
  data-disabled={disabled}
  data-text-align={textAlign}
  data-allow-icon-own-color={allowIconOwnColor}
  aria-describedby={ariaDescribedby}
  {...$$restProps}
  on:click={onClick}
  on:keydown={(event) => {
    if (event.key === " " || event.key === "Enter") {
      onClick(event)
    }
  }}
  on:focus
  on:blur
  on:mouseover
  on:mouseenter
  on:mouseleave
  use:combinedActions
>
  {#if $$slots.leftIcon}
    <span class="au-button__left-icon">
      <slot name="leftIcon" />
    </span>
  {/if}
  <div class="au-button__content">
    <div class="au-button__main-content"><slot /></div>
  </div>
  {#if $$slots.rightIcon}
    <span class="au-button__right-icon">
      <slot name="rightIcon" />
    </span>
  {/if}
</span>

<style>
  .au-button {
    font-size: var(--au-text-button-font-size-base);
    line-height: var(--au-text-button-line-height-base);
    font-weight: var(--au-text-weight-medium);

    border-radius: var(--au-button-border-radius);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    transition: all var(--au-transition-fast);
    padding: var(--au-button-padding);
    cursor: pointer;
    height: var(--au-button-height);
    gap: var(--au-button-gap);
    width: fit-content;

    /* Color props */
    background-color: var(--au-button-background-color);
    color: var(--au-button-color);
    border: var(--au-line-width-default) solid var(--au-button-border-color);
  }

  .au-button:hover {
    background-color: var(--au-button-hover-background-color);
    border-color: var(--au-button-hover-border-color);
    color: var(--au-button-hover-color);
  }

  .au-button:active {
    background-color: var(--au-button-active-background-color);
    border-color: var(--au-button-active-border-color);
    color: var(--au-button-hover-color);
  }

  .au-button:focus-visible {
    outline: var(--au-focus-outline-width) solid var(--au-focus-outline-color);
    outline-offset: calc(-1 * var(--au-focus-outline-width));
  }

  .au-button[data-text-align="left"] {
    text-align: left;
  }

  .au-button[data-text-align="left"] .au-button__content {
    flex-grow: 1;
  }

  .au-button[data-disabled="true"] {
    opacity: var(--au-disabled-opacity);
    cursor: not-allowed;
    pointer-events: none;
  }

  .au-button[data-full-width="true"] {
    width: 100%;
  }

  .au-button[data-size="sm"] {
    --au-button-height: var(--au-tappable-size-sm);
    --au-button-padding: 0 var(--au-spacing-xs);
    --au-button-gap: var(--au-spacing-2xs);
  }

  .au-button[data-size="md"] {
    --au-button-height: var(--au-tappable-size);
    --au-button-padding: var(--au-spacing-2xs) var(--au-spacing-md);
    --au-button-gap: var(--au-spacing-sm);
  }

  .au-button[data-size="lg"] {
    --au-button-height: var(--au-tappable-size-lg);
    --au-button-padding: var(--au-spacing-xs) var(--au-spacing-md);
    --au-button-gap: var(--au-spacing-md);
  }

  .au-button[data-color="primary"] {
    --au-button-background-color: var(--au-button-primary-background-color);
    --au-button-border-color: var(--au-button-primary-border-color);
    --au-button-color: var(--au-button-primary-color);
    --au-button-hover-background-color: var(
      --au-button-primary-hover-background-color
    );
    --au-button-active-background-color: var(
      --au-button-primary-active-background-color
    );
    --au-button-hover-border-color: var(--au-button-primary-hover-border-color);
    --au-button-hover-color: var(--au-button-primary-hover-color);
    --au-button-active-border-color: var(--button-primary-active-border-color);
    --au-button-icon-color: var(--au-button-primary-icon-color);
    --au-button-hover-icon-color: var(--au-button-primary-hover-icon-color);
    --au-focus-outline-color: var(--au-button-primary-icon-color);
  }

  .au-button[data-color="secondary"] {
    --au-button-background-color: var(--au-button-secondary-background-color);
    --au-button-hover-background-color: var(
      --au-button-secondary-hover-background-color
    );
    --au-button-active-background-color: var(
      --au-button-secondary-active-background-color
    );
    --au-button-border-color: var(--au-button-secondary-border-color);
    --au-button-hover-border-color: var(
      --au-button-secondary-hover-border-color
    );
    --au-button-active-border-color: var(
      --au-button-secondary-active-border-color
    );
    --au-button-color: var(--au-button-secondary-color);
    --au-button-hover-color: var(--au-button-secondary-hover-color);
    --au-button-icon-color: var(--au-button-secondary-icon-color);
    --au-button-hover-icon-color: var(--au-button-secondary-hover-icon-color);
    --au-focus-outline-color: var(--au-button-secondary-icon-color);
  }

  .au-button[data-color="link"] {
    --au-button-background-color: var(--au-button-link-background-color);
    --au-button-hover-background-color: var(
      --au-button-link-hover-background-color
    );
    --au-button-active-background-color: var(
      --au-button-link-active-background-color
    );
    --au-button-border-color: var(--au-button-link-border-color);
    --au-button-hover-border-color: var(--au-button-link-hover-border-color);
    --au-button-active-border-color: var(--au-button-link-active-border-color);
    --au-button-color: var(--au-button-link-color);
    --au-button-hover-color: var(--au-button-link-hover-color);
    --au-button-icon-color: var(--au-button-link-icon-color);
    --au-button-hover-icon-color: var(--au-button-link-hover-icon-color);
    --au-focus-outline-color: var(--au-button-link-icon-color);
  }

  .au-button .au-button__left-icon,
  .au-button .au-button__right-icon {
    display: inline-flex;
    width: var(--au-icon-width-md);
  }

  /**
   * Make Safari behave
   * @see https://stackoverflow.com/questions/24899943/how-do-i-make-flex-box-work-in-safari#answer-59348265
   */
  .au-button__left-icon :global(svg),
  .au-button__right-icon :global(svg) {
    flex: 1 0 auto;
  }

  .au-button[data-allow-icon-own-color="false"]
    .au-button__left-icon
    :global(svg),
  .au-button[data-allow-icon-own-color="false"]
    .au-button__right-icon
    :global(svg) {
    stroke: var(--au-button-icon-color);
    fill: var(--au-button-icon-color);
  }

  .au-button[data-allow-icon-own-color="false"]:hover
    .au-button__left-icon
    :global(svg),
  .au-button[data-allow-icon-own-color="false"]:hover
    .au-button__right-icon
    :global(svg) {
    stroke: var(--au-button-hover-icon-color);
    fill: var(--au-button-hover-icon-color);
  }

  /* Ensure color doesn't leak from other page rules */
  .au-button__content,
  .au-button__main-content {
    color: inherit;
  }
</style>
